import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { retrieveAuthenticatedUserInfo } from "../utils/authService";
import { getCurrentSelectedTenant } from "../utils/user";
import { segment } from "../utils/segment";
import { mixpanel } from "../utils/mixpanel";

interface AnalyticsProvider {
  identify: (directoryUuid: string | undefined) => void;
  track: (eventName: string, properties: object) => void;
  page: (properties: object) => void;
  reset: () => void;
}

const defaultProperties = {};

const getUserProperties = (provider: AnalyticsProvider) => {
  const directoryUuid = retrieveAuthenticatedUserInfo()?.userId;
  // Establish UUID identity before sending the user's next event.
  provider.identify(directoryUuid);
  return {
    tenant: getCurrentSelectedTenant(),
    ...(directoryUuid ? { directory_uuid: directoryUuid } : {}),
  };
};

export const useAnalytics = () => {
  const rollout = import.meta.env.VITE_MIXPANEL_ROLLOUT;
  const tenant = getCurrentSelectedTenant();
  // Advance each environment from off -> securityjourney -> all, validating dev first.
  const useMixpanel =
    rollout === "all" || (rollout === "securityjourney" && tenant === "securityjourney");
  const provider: AnalyticsProvider =
    useMixpanel && import.meta.env.VITE_MIXPANEL_TOKEN ? mixpanel : segment;
  const directoryUuid = retrieveAuthenticatedUserInfo()?.userId;

  useEffect(() => {
    provider.identify(directoryUuid);
  }, [provider, directoryUuid]);

  const track = useCallback(
    (eventName: string, properties: object = {}) => {
      provider.track(eventName, { ...properties, ...getUserProperties(provider) });
    },
    [provider]
  );

  // Ordinary renders must not change the page callback and retrigger page views.
  const page = useCallback(
    (properties: object = {}) => {
      provider.page({ ...properties, ...getUserProperties(provider) });
    },
    [provider]
  );

  return { track, page, reset: provider.reset };
};

export const useAnalyticsPage = (additionalProperties = defaultProperties) => {
  const { page } = useAnalytics();
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Changing tenant/provider is not a new page navigation.
    if (lastTrackedPath.current === location.pathname) return;
    lastTrackedPath.current = location.pathname;
    page(additionalProperties);
  }, [location.pathname, additionalProperties, page]);
};

export const useAnalyticsTrackEvent = () => {
  return useAnalytics().track;
};
